/**
 * Shared meta tag builder for consistent SEO across all public routes.
 * Returns a full array of meta descriptors including OG, Twitter, and canonical tags.
 */

const SITE_NAME = 'Ignite Identity'
const DEFAULT_OG_IMAGE = 'https://igniteidentity.nl/og-default.jpg'
const BASE_URL = 'https://igniteidentity.nl'

interface BuildMetaOptions {
	title: string
	description: string
	url: string
	image?: string | null
	imageAlt?: string | null
	type?: 'website' | 'article'
	publishedAt?: string | null
	author?: string | null
	noIndex?: boolean
}

export function buildMeta({
	title,
	description,
	url,
	image,
	imageAlt,
	type = 'website',
	publishedAt,
	author,
	noIndex,
}: BuildMetaOptions) {
	const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
	const ogImage = image || DEFAULT_OG_IMAGE
	const fullTitle = title.includes(SITE_NAME) ? title : `${title} - ${SITE_NAME}`
	const ogImageAlt = imageAlt || title

	const meta: Array<Record<string, string>> = [
		{ title: fullTitle },
		{ name: 'description', content: description },
		// Open Graph
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: fullUrl },
		{ property: 'og:type', content: type },
		{ property: 'og:site_name', content: SITE_NAME },
		{ property: 'og:locale', content: 'nl_NL' },
		...(ogImage
			? [
					{ property: 'og:image', content: ogImage },
					{ property: 'og:image:width', content: '1200' },
					{ property: 'og:image:height', content: '630' },
					{ property: 'og:image:alt', content: ogImageAlt },
				]
			: []),
		// Twitter Card
		{ name: 'twitter:card', content: ogImage ? 'summary_large_image' : 'summary' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		...(ogImage ? [{ name: 'twitter:image', content: ogImage }] : []),
		// Canonical
		{ tagName: 'link', rel: 'canonical', href: fullUrl },
		// Hreflang (self-referencing for geo-targeting)
		{ tagName: 'link', rel: 'alternate', hreflang: 'nl', href: fullUrl },
		{ tagName: 'link', rel: 'alternate', hreflang: 'x-default', href: fullUrl },
	]

	// Article-specific tags
	if (type === 'article') {
		if (publishedAt) {
			meta.push({ property: 'article:published_time', content: publishedAt })
		}
		if (author) {
			meta.push({ property: 'article:author', content: author })
		}
	}

	if (noIndex) {
		meta.push({ name: 'robots', content: 'noindex, nofollow' })
	}

	return meta
}

/**
 * Build JSON-LD structured data for WebSite with SearchAction (enables sitelinks search box).
 */
export function buildWebSiteJsonLd() {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			name: SITE_NAME,
			url: BASE_URL,
			inLanguage: 'nl',
			potentialAction: {
				'@type': 'SearchAction',
				target: {
					'@type': 'EntryPoint',
					urlTemplate: `${BASE_URL}/resources/search?q={search_term_string}`,
				},
				'query-input': 'required name=search_term_string',
			},
		},
	}
}

/**
 * Build JSON-LD structured data for Organization (used on home page).
 */
export function buildOrganizationJsonLd() {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'Organization',
			name: SITE_NAME,
			url: BASE_URL,
			logo: `${BASE_URL}/favicons/android-chrome-512x512.png`,
			// TODO: Add social media profile URLs once they exist
		sameAs: [],
		},
	}
}

/**
 * Build JSON-LD structured data for a NewsArticle.
 */
export function buildNewsArticleJsonLd(article: {
	title: string
	description: string
	url: string
	image?: string | null
	publishedAt?: string | null
	modifiedAt?: string | null
	author?: string | null
}) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'NewsArticle',
			headline: article.title,
			description: article.description,
			url: article.url.startsWith('http') ? article.url : `${BASE_URL}${article.url}`,
			...(article.image && { image: article.image }),
			...(article.publishedAt && { datePublished: article.publishedAt }),
			...(article.modifiedAt && { dateModified: article.modifiedAt }),
			author: {
				'@type': 'Organization',
				name: article.author || SITE_NAME,
			},
			publisher: {
				'@type': 'Organization',
				name: SITE_NAME,
				logo: {
					'@type': 'ImageObject',
					url: `${BASE_URL}/favicons/android-chrome-512x512.png`,
				},
			},
		},
	}
}

/**
 * Build JSON-LD structured data for a Service.
 */
export function buildServiceJsonLd(service: {
	name: string
	description: string
	url: string
}) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'Service',
			name: service.name,
			description: service.description,
			url: service.url.startsWith('http') ? service.url : `${BASE_URL}${service.url}`,
			provider: {
				'@type': 'Organization',
				name: SITE_NAME,
			},
		},
	}
}

/**
 * Build JSON-LD structured data for a CreativeWork (case study).
 */
export function buildCaseStudyJsonLd(caseStudy: {
	title: string
	description: string
	url: string
	image?: string | null
	client?: string | null
}) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'CreativeWork',
			name: caseStudy.title,
			description: caseStudy.description,
			url: caseStudy.url.startsWith('http') ? caseStudy.url : `${BASE_URL}${caseStudy.url}`,
			...(caseStudy.image && { image: caseStudy.image }),
			creator: {
				'@type': 'Organization',
				name: SITE_NAME,
			},
			...(caseStudy.client && {
				about: {
					'@type': 'Organization',
					name: caseStudy.client,
				},
			}),
		},
	}
}

/**
 * Build JSON-LD BreadcrumbList.
 */
export function buildBreadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: items.map((item, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: item.name,
				item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
			})),
		},
	}
}
